import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import deepcopy from 'deepcopy';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Grid } from '@progress/kendo-react-grid';
import Toast from '@hobs/components/Toast';
import GridLoader from '@hobs/components/Grid/GridLoader';
import Columns from '@hobs/components/Grid/Columns';
import WithRoles from '@hobs/hocs/WithRoles';
import {
  withDataStateLocalOverrides,
  withGridLocalOverrides,
  onUserResetSettings
} from '@hobs/utils/grid/overrides';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import { H1 } from '@hobs/components/Headings';
import { createLoadingSelector } from '@hobs/utils/loading';
import Confirm from '@hobs/components/Confirm';
import { queryOData, resetOData } from '@hobs/actions/odata';
import { deleteSubcontractor } from '@hobs/actions/subcontractor';
import { PAGE_SIZES } from '@hobs/utils/constants';
import SubcontractorDialog from '../SubcontractorDialog/SubcontractorDialog';
import './Subcontractors.scss';

class Subcontractors extends React.PureComponent {
  LSdataStateName = `Subcontractor${this.props.user.CurrentExerciseId}.dataState`;
  LSGridPropsName = `Subcontractor${this.props.user.CurrentExerciseId}.grid.props`;
  state = {
    dataState: {
      take: 20,
      skip: 0,
      sort: [{ field: 'OrganizationCode', dir: 'asc' }]
    }
  };

  onUserSaveSettings = () => {
    localStorage.setItem(
      this.LSdataStateName,
      JSON.stringify(this.state.dataState)
    );
    localStorage.setItem(
      this.LSGridPropsName,
      JSON.stringify({
        columns: this.grid.current.columns
      })
    );
  };

  onUserResetSettings = onUserResetSettings(
    this.LSdataStateName,
    this.LSGridPropsName,
    null,
    () => {
      const columns = this.getGridColumns();

      this.setState(
        {
          gridKey: ShortId.generate(),
          columns,
          dataState: this.defaultDataState
        },
        () => {
          this.queryOData();
        }
      );
    }
  );

  constructor(props) {
    super(props);

    this.exportRef = React.createRef();
    this.grid = React.createRef();

    this.defaultGridColumns = this.getGridColumns();

    this.defaultDataState = {
      take: 20,
      sort: [{ field: 'OrganizationCode', dir: 'acs' }],
      skip: 0
    };
    const defaultState = {
      dataState: withDataStateLocalOverrides(
        deepcopy(this.defaultDataState),
        this.LSdataStateName
      ),
      gridKey: ShortId.generate(),
      customFilters: {},
      columns: withGridLocalOverrides(
        deepcopy(this.defaultGridColumns),
        this.LSGridPropsName
      ),
      openDialog: false,
      selectedRows: {},
      isAllSelected: false,
      previousWindowScrollOffset: null
    };

    this.state = {
      ...defaultState
    };
  }

  componentDidMount() {
    this.queryOData();
  }

  componentWillUnmount() {
    this.props.resetOData();
  }

  queryOData = () => {
    this.props.queryOData('SuppliersSubcontractorQuery', this.state.dataState);
  };

  dataStateChange = event => {
    this.setState(
      {
        dataState: event.dataState
      },
      () => {
        this.queryOData();
      }
    );
  };

  getCreatePathName() {
    return `${this.props.location.pathname.replace('/create', '')}/create`;
  }

  getEditPathName(itemId) {
    return `${this.props.location.pathname.replace(
      `/edit/${itemId}`,
      ''
    )}/edit/${itemId}`;
  }

  showDeleteDialog = subcontractorsItemId => {
    Confirm(
      this.props.t('Remove subcontractor'),
      this.props.t('Are you sure you want to remove this subcontractor?'),
      () => {
        const { deleteSubcontractor } = this.props;

        deleteSubcontractor(subcontractorsItemId).then(response => {
          if (response.success) {
            Toast.success(this.props.t('Subcontractor deleted successfully'));

            this.queryOData();
          } else {
            Toast.warning(response.payload.message);
          }
        });
      }
    );
  };

  getGridColumns() {
    const { t } = this.props;

    const columns = {
      OrganizationCode: {
        default: true,
        visible: true,
        title: t('Organization Code'),
        columnMenu: true
      },
      OrganizationName: {
        default: true,
        visible: true,
        title: t('Organization Name'),
        columnMenu: true
      },
      Address: {
        default: true,
        visible: true,
        title: t('Address'),
        columnMenu: true
      },
      Email: {
        default: true,
        visible: true,
        title: t('Email'),
        columnMenu: true
      },
      Phone: {
        default: true,
        visible: true,
        title: t('Phone'),
        columnMenu: true
      },
      Notes: {
        default: true,
        visible: true,
        title: t('Notes'),
        columnMenu: true
      },
      IsActive: {
        default: true,
        visible: true,
        title: t('Is Active'),
        columnMenu: true,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsActive ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              value: '',
              default: true
            },
            {
              name: t('Yes'),
              value: true,
              default: false
            },
            {
              name: t('No'),
              value: false,
              default: false
            }
          ]
        }
      }
    };
    Object.assign(columns, {
      edit: {
        default: true,
        visible: true,
        resizable: false,
        width: '110px',
        headerCell: () => <></>,
        cell: props => (
          <td className="subcontractors-view--button--column" colSpan="1">
            <GhostButton
              iconLeft={<Icon icon="Pen" />}
              to={this.getEditPathName(props.dataItem.SubcontractorId)}
            >
              {t('Edit')}
            </GhostButton>
          </td>
        )
      },
      delete: {
        default: true,
        visible: true,
        resizable: false,
        width: '116px',
        headerCell: () => <></>,
        cell: props => (
          <td className="subcontractors-view--button--column" colSpan="1">
            <GhostButton
              iconLeft={<Icon icon="Delete" />}
              onClick={() =>
                this.showDeleteDialog(props.dataItem.SubcontractorId)
              }
            >
              {t('Delete')}
            </GhostButton>
          </td>
        )
      }
    });

    return columns;
  }

  render() {
    const { t, match, location, history } = this.props;

    return (
      <div className="subcontractors-view">
        <H1>{t('Subcontractors')}</H1>
        <div className="subcontractors-view--heading">
          <div className="subcontractors-view--left">
            <RaisedButton
              iconLeft={<Icon icon="plus" />}
              to={this.getCreatePathName()}
            >
              {t('Add new subcontractor')}
            </RaisedButton>
          </div>
        </div>
        <div>
          <SubcontractorDialog
            location={location}
            match={match}
            history={history}
            onSuccess={this.queryOData}
          />
          <Grid
            resizable={true}
            sortable={true}
            key={this.state.gridKey}
            ref={this.grid}
            pageable={{
              pageSizes: PAGE_SIZES
            }}
            {...this.state.dataState}
            {...this.props.oData}
            onDataStateChange={this.dataStateChange}
          >
            {Columns({
              columns: this.state.columns,
              onSave: this.onUserSaveSettings,
              onReset: this.onUserResetSettings,
              dataState: this.state.dataState
            })}
          </Grid>
          {this.props.isLoading && <GridLoader />}
        </div>
      </div>
    );
  }
}

Subcontractors.propTypes = {
  isLoading: PropTypes.bool,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  oData: PropTypes.object,
  user: PropTypes.object,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  deleteSubcontractor: PropTypes.func,
  t: PropTypes.func
};

const oDataLoading = createLoadingSelector(['OData']);
const mapStateToProps = state => ({
  isLoading: oDataLoading(state),
  oData: state.odata
});

const mapDispatchToProps = {
  queryOData,
  resetOData,
  deleteSubcontractor
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withRouter(WithRoles(Subcontractors))));
